<i18n>
{
	"en": {
		"found": "{count} found"
	},
	"no": {
		"found": "{count} treff"
	}
}
</i18n>

<template>
	<t-form-group
		:title="title"
		rowGap="5px"
	>
		<template #header>
			<slot name="header" />
		</template>

		<t-popover
			:showPopover="shouldShowPopover"
			:marginY="6"
			style="width: 100%;"
			position="bottom"
			:min-width="150"
			relative
			v-bind="$attrs"
		>

			<t-text-input-button
				ref="textInputButton"
				slot="target"
				:value="searchValue"
				:icon="icon"
				:disabled="disabled"
				:buttonDisabled="buttonDisabled"
				:disableButtonTabbing="!enableButtonTabbing"
				:isInvalid="isInvalid"
				:validationMessage="validationMessage"

				@focus="onFocus"
				@input="onInput"
				@blur="onBlur"
				@button:click="$emit('button:click', $event)"
			/>

			<t-menu-options-group
				v-if="items"
				ref="menuOptions"
				#default="slotProps"
				style="box-sizing: border-box; max-height: 200px;"
				groupStyle="combobox"
				:title="showCount
					? $tc('found', items.length, { count: items.length })
					: null"
				disableSpaceToSelect

				:items="items"
				:selectedItem="selected"

				@change="onChange"
			>

				<t-menu-combobox-option-item
					#default
					:value="slotProps.item"
					:selected="slotProps.isSelected"
					:focused="slotProps.isFocused"
					height="40px"
				>
					<slot
						name="optionItemContent"
						:item="slotProps.item"
					>
						{{ displayKey
							? slotProps.item[displayKey]
							: slotProps.item }}
					</slot>
				</t-menu-combobox-option-item>
			</t-menu-options-group>

		</t-popover>
	</t-form-group>
</template>

<script>
import TTextInputButton from '@/components/evergreen/inputs/TTextInputButton';
import TMenuComboboxOptionItem from '@/components/evergreen/menu/TMenuComboboxOptionItem';
import TMenuOptionsGroup from '@/components/evergreen/menu/TMenuOptionsGroup';
import popoverMixin from '@/components/evergreen/inputs/popoverMixin';
import TPopover from '@/components/evergreen/overlays/TPopover';
import TFormGroup from '@/components/evergreen/timpex/inputs/TFormGroup';

export default {
	name: 'TComboboxButton',
	components: { TFormGroup, TPopover, TMenuOptionsGroup, TMenuComboboxOptionItem, TTextInputButton },
	mixins: [ popoverMixin ], // TODO: maybe remove mixin
	props: {
		searchValue: {
			type: String,
			required: true
		},
		title: String,
		items: {
			type: Array,
			default: () => [],
		},
		icon: {
			type: String,
			default: 'geosearch'
		},
		disabled: {
			type: Boolean,
			default: false
		},
		buttonDisabled: Boolean,
		enableButtonTabbing: Boolean,
		selected: Object,
		displayKey: {
			type: String,
			required: false, // Only required when not using optionItemContent slot
		},
		isInvalid: {
			type: Boolean,
			default: false,
		},
		validationMessage: String,
		showCount: {
			type: Boolean,
			default: true
		},
		showPopoverOnFocus: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			focused: false,
		};
	},

	computed: {
		shouldShowPopover() {

			if (this.showPopoverOnFocus)
				return this.focused;

			else
				return this.popoverIsVisible
					&& this.searchValue.length > 0
					&& this.items.length > 0
					&& this.focused;
		}
	},

	methods: {
		focus() {
			this.$refs.textInputButton.$refs.input.focus();
		},
		onFocus(event) {
			this.focused = true;

			this.popoverSetVisible();
			event.target.select();
			this.$emit('focus', event);
		},
		onChange(value) {

			this.popoverSetHidden();
			this.$emit('select', value);
			this.$emit('change', value);
		},
		onInput(event) {
			this.popoverSetVisible();

			this.$emit('input', event);
		},
		onBlur(event) {

			if (isNextFocusInMenuOptions(event, this.$refs.menuOptions)) {

				event.target.focus();
				return;
			}

			this.focused = false;
			this.popoverSetHidden();
			this.$emit('blur', event);

			function isNextFocusInMenuOptions(event, menuOptionsRef) {
				return event.relatedTarget
					&& menuOptionsRef
					&& menuOptionsRef.$el
					&& menuOptionsRef.$el.contains(event.relatedTarget);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.item {
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
}

.item-main-text {
	height:16px;
	line-height: 16px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
}

.item-sub-text {
	height:16px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}

.details {
	@extend %text-small;
	color: get-color($text, 'muted');
	padding-left: 10px;
}
</style>
